import {useState, useEffect} from "react";
import {Loader2, Wifi, RefreshCw} from "lucide-react";

const LoadingSpinner = ({
  size = "large",
  message = "Loading...",
  fullScreen = true,
  className = "",
  showProgress = false,
  timeout = 10000, // 10 seconds timeout
  onTimeout = null,
}) => {
  const [dots, setDots] = useState("");
  const [showTimeoutMessage, setShowTimeoutMessage] = useState(false);

  const sizeClasses = {
    small: "h-6 w-6",
    medium: "h-8 w-8",
    large: "h-12 w-12",
    xl: "h-16 w-16",
  };

  const containerClasses = fullScreen
    ? "flex items-center justify-center min-h-screen bg-gray-50"
    : "flex items-center justify-center p-4";

  // Animate dots
  useEffect(() => {
    const interval = setInterval(() => {
      setDots((prev) => {
        if (prev === "...") return "";
        return prev + ".";
      });
    }, 500);

    return () => clearInterval(interval);
  }, []);

  // Handle timeout
  useEffect(() => {
    if (timeout > 0) {
      const timer = setTimeout(() => {
        setShowTimeoutMessage(true);
        if (onTimeout) {
          onTimeout();
        }
      }, timeout);

      return () => clearTimeout(timer);
    }
  }, [timeout, onTimeout]);

  return (
    <div className={`${containerClasses} ${className}`}>
      <div className="text-center max-w-md mx-auto px-4">
        <div className="mb-6 flex justify-center">
          <Loader2
            className={`animate-spin text-blue-600 ${sizeClasses[size]}`}
          />
        </div>

        <div className="space-y-3">
          <h3 className="text-xl font-semibold text-gray-800">
            {message}
            {dots}
          </h3>

          <p className="text-sm text-gray-600">
            Please wait while we load the page...
          </p>

          {showProgress && (
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-blue-600 h-2 rounded-full animate-pulse"
                style={{width: "60%"}}
              ></div>
            </div>
          )}

          {showTimeoutMessage && (
            <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
              <p className="text-sm text-yellow-800">
                This is taking longer than expected. Please check your internet
                connection.
              </p>
            </div>
          )}
        </div>

        {/* Loading tips */}
        <div className="mt-8 text-xs text-gray-500 space-y-1">
          <p className="flex items-center gap-2">
            <RefreshCw className="w-3 h-3" />
            <strong>Tip:</strong> If loading takes too long, try refreshing the
            page
          </p>
          <p className="flex items-center gap-2">
            <Wifi className="w-3 h-3" />
            <strong>Network:</strong> Check your internet connection
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoadingSpinner;
